import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { API_CONFIG } from '../config/api';
import '../styles/pages/_modules.scss';
import { Briefcase, Award, Users, Lightbulb, CheckCircle } from 'lucide-react';

const occupationalWellnessImage = require('../assets/physical.png');

interface ModuleCard {
  id: string;
  title: string;
  category: 'occupationalM1' | 'occupationalM2' | 'occupationalM3' | 'occupationalM4' | 'occupationalM5';
  image: string;
  icon: React.ReactNode;
}

interface ModuleProgress {
  moduleId: string;
  currentStep: number;
  completed: boolean;
}

const OccupationalResourcesPage: React.FC = () => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [progress, setProgress] = useState<ModuleProgress[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchProgress = async () => {
      if (!user || !token) return;
      try {
        const moduleIds = [
          'occupationalM1',
          'occupationalM2',
          'occupationalM3',
          'occupationalM4',
          'occupationalM5'
        ];
        const responses = await Promise.all(
          moduleIds.map(moduleId =>
            fetch(`${API_CONFIG.ENDPOINTS.MODULE_PROGRESS}/${moduleId}`, {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              credentials: 'include'
            })
          )
        );
        const results = await Promise.all(
          responses.map(async (response, index) => {
            if (response.ok) {
              const data = await response.json();
              return {
                moduleId: moduleIds[index],
                ...data.data
              };
            }
            return {
              moduleId: moduleIds[index],
              currentStep: 0,
              completed: false
            };
          })
        );
        setProgress(results);
      } catch (error) {

      } finally {
        setIsLoading(false);
      }
    };
    fetchProgress();
  }, [user, token]);

  const handleModuleClick = (moduleId: string) => {
    switch (moduleId) {
      case 'm1':
        navigate('/panel/learn/occupational-module-1');
        break;
      case 'm2':
        navigate('/panel/learn/occupational-module-2');
        break;
      case 'm3':
        navigate('/panel/learn/occupational-module-3');
        break;
      case 'm4':
        navigate('/panel/learn/occupational-module-4');
        break;
      case 'm5':
        navigate('/panel/learn/occupational-module-5');
        break;
      default:
        console.log(`Module ${moduleId} navigation not yet implemented`);
    }
  };

  const modules: ModuleCard[] = [
    {
      id: 'm1',
      title: 'Understanding Occupational Wellness',
      category: 'occupationalM1',
      image: occupationalWellnessImage.default || occupationalWellnessImage,
      icon: <Briefcase size={24} />,
    },
    {
      id: 'm2',
      title: 'Recognizing and Preventing Burnout',
      category: 'occupationalM2',
      image: occupationalWellnessImage.default || occupationalWellnessImage,
      icon: <Award size={24} />,
    },
    {
      id: 'm3',
      title: 'Reigniting Work Engagement',
      category: 'occupationalM3',
      image: occupationalWellnessImage.default || occupationalWellnessImage,
      icon: <Users size={24} />,
    },
    {
      id: 'm4',
      title: 'Building a Healthy Work Environment',
      category: 'occupationalM4',
      image: occupationalWellnessImage.default || occupationalWellnessImage,
      icon: <Lightbulb size={24} />,
    },
    {
      id: 'm5',
      title: 'Designing Work-Life Harmony and Purpose',
      category: 'occupationalM5',
      image: occupationalWellnessImage.default || occupationalWellnessImage,
      icon: <Award size={24} />,
    }
  ];

  if (isLoading) {
    return (
      <div className={`resources-page ${isDarkMode ? 'dark' : ''}`} style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading your progress...</p>
      </div>
    );
  }

  return (
    <div className={`resources-page ${isDarkMode ? 'dark' : ''}`}>
      <div className="module-resources_container">
        {/* Header Section */}
        <div className="module-resources_header">
          <div className="module-resources_header-content">
            <div className="module-resources_header-text">
              <h2>Occupational Wellness Resources</h2>
            </div>
          </div>
        </div>
        {/* Modules Grid */}
        <div className="module-resources_modules">
          <div className="module-resources_modules-grid">
            {modules.length > 0 ?
              modules.map((module: ModuleCard) => {
                const moduleProgress = progress.find(p => p.moduleId === module.category);
                const isCompleted = moduleProgress?.completed || false;
                return (
                  <div
                    key={module.id}
                    className={`module-resources_module-card ${isCompleted ? 'completed' : ''}`}
                    onClick={() => handleModuleClick(module.id)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        handleModuleClick(module.id);
                      }
                    }}
                  >
                    <div className="module-resources_module-image">
                      <img src={module.image} alt={module.title} />
                      {isCompleted && (
                        <div className="completion-overlay">
                          <CheckCircle
                            className="check-icon"
                            strokeWidth={1.5}
                            absoluteStrokeWidth
                          />
                        </div>
                      )}
                    </div>
                    <div className="module-resources_module-content">
                      <h3 className="module-resources_module-title">{module.title}</h3>
                    </div>
                  </div>
                );
              })
              : <p>No modules found.</p>
            }
          </div>
        </div>
      </div>
    </div>
  );
};

export default OccupationalResourcesPage;


